import * as React from 'react';

import { useTranslation } from 'react-i18next';

import { Button } from '@shared/components/button';
import { Modal } from '@shared/components/modal';
import { useModals } from '@shared/contexts/modal-context';

import { ModalName } from '../types';
import * as Styled from './confirm-modal.styled.ts';

export interface ConfirmModalData {
	title?: string;
	message?: string;
	confirmButtonText?: string;
	cancelButtonText?: string;
	onConfirm?: () => void | Promise<void>;
	onCancel?: () => void;
	variant?: 'warning' | 'info' | 'success' | 'error';
}

const ConfirmModal = () => {
	const { isModalOpen, closeModal, getModalData } = useModals();
	const { t } = useTranslation(['main', 'help', 'welcome']);
	const [isSubmitting, setIsSubmitting] = React.useState(false);

	const confirmData = getModalData<ConfirmModalData>(ModalName.CONFIRM) || {};

	const {
		title = t('confirm_action'),
		message = t('are_you_sure'),
		confirmButtonText = 'Выйти',
		cancelButtonText = 'Закрыть',
		onConfirm,
		onCancel
	} = confirmData;

	const handleModalClose = React.useCallback(() => {
		if (isSubmitting) {
			return;
		}
		closeModal();
	}, [closeModal, isSubmitting]);

	const handleCancel = React.useCallback(() => {
		if (isSubmitting) {
			return;
		}
		if (onCancel) {
			onCancel();
		}
		closeModal();
	}, [onCancel, closeModal, isSubmitting]);

	const handleConfirm = React.useCallback(async () => {
		if (isSubmitting) {
			return;
		}

		try {
			setIsSubmitting(true);
			await Promise.resolve(onConfirm?.());
			closeModal();
		} catch (error) {
			console.error('Failed to confirm action:', error);
		} finally {
			setIsSubmitting(false);
		}
	}, [onConfirm, closeModal, isSubmitting]);

	return (
		<Modal isOpen={isModalOpen(ModalName.CONFIRM)} title={title} closeModal={handleModalClose}>
			<Styled.Wrapper>
				<Styled.Message>{message}</Styled.Message>

				<Styled.Buttons>
					<Button onClick={handleConfirm} disabled={isSubmitting}>
						{isSubmitting ? t('loading_common') : confirmButtonText}
					</Button>
					<Button onClick={handleCancel} color='dark' disabled={isSubmitting}>
						{cancelButtonText}
					</Button>
				</Styled.Buttons>
			</Styled.Wrapper>
		</Modal>
	);
};

export default ConfirmModal;
